import Button from "Components/Button";
import { OrderDetails } from "hooks/OrderManagement/useOrders";
import { DeliveryType } from "types/deliveryType.enum";
import { OrderStatusEnum } from "types/orderStatus.enum";
import { AcceptOrder } from "./AcceptOrder";
import { DeclineOrder } from "./DeclineOrder";
import { MarkAsShipped } from "./MarkAsShipped";
import { OrderDriverSelection } from "./OrderDriverSelection";
import { useMarkOrderAsMissedMutation } from "hooks/OrderManagement/useMarkOrderAsMissedMutation";
import { useState } from "react";
import Textarea from "Components/Textarea";
import { Modal } from "antd";
import { useAdminMarkAsDelivered } from "hooks/Driver/useMarkAsDelivered";

type OrderDetailsItemActionProps = {
  data?: OrderDetails;
};

export const OrderDetailsItemAction = ({
  data,
}: OrderDetailsItemActionProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [note, setNote] = useState("");

  const { mutateAsync, isPending } = useMarkOrderAsMissedMutation({
    id: data?.id,
    note: "",
    setModalOpen,
  });

  const handleMarkAsMissed = () => {
    mutateAsync();
  };

  const { mutateAsync: handleDelivered, isPending: isDelivering } =
    useAdminMarkAsDelivered({
      id: data?.id ? data?.id : "",
    });

  const [otp, setOtp] = useState("");

  const handlesSubmit = () => {
    handleDelivered(otp, {
      onSuccess: () => {
        setOtp("");
        setModalOpen(false);
      },
    });
  };

  if (!data) return null;

  const isPickup = data.deliveryMethod === DeliveryType.SELF_DELIVERY;
  const hasCustomerPaid = data.paymentStatus === "paid";

  if (data?.orderStatus === OrderStatusEnum.PLACED) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">Items are available in stock</p>
        <div className="flex gap-2 whitespace-nowrap">
          <AcceptOrder data={data} />
          <DeclineOrder data={data} />
        </div>
      </div>
    );
  }

  if (data?.orderStatus === OrderStatusEnum.PROCESSING && !hasCustomerPaid) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">
          Awaiting Payment from customer
        </p>
        <div className="">
          {isPickup ? (
            <Button label="Mark as picked up" disabled />
          ) : (
            <Button label="Assign for shipping" disabled />
          )}
        </div>
      </div>
    );
  }

  if (
    data?.statusLogs &&
    data?.statusLogs[data.statusLogs.length - 1]?.status === "transit"
  ) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">Awaiting pickup by customer</p>
        <div className="">
          <Button label="Mark as Picked" onClick={() => setModalOpen(true)} />
          <div>
            <Modal
              open={modalOpen}
              onCancel={() => setModalOpen(false)}
              footer={null}
              title="Mark Order as picked up"
            >
              <div className="py-2">
                <p className="text-primary-foreground text-lg">
                  Are you sure you want to mark this order as picked up, this
                  action can not be reversed
                </p>

                <div className="mt-3">
                  <input
                    type="text"
                    value={otp}
                    maxLength={4}
                    disabled={isDelivering}
                    placeholder="Enter OTP"
                    onChange={(e: any) => setOtp(e?.target?.value)}
                    className="p-3 mb-3 text-[16px] bg-white w-full border-[1px] duration-300 border-[#d3d3d3] focus:border-orange rounded-md"
                  />
                </div>

                <div className="flex space-x-2">
                  <Button
                    label="Yes"
                    isLoading={isDelivering}
                    onClick={handlesSubmit}
                  />
                  <Button
                    label="No"
                    onClick={() => setModalOpen(false)}
                    className="bg-[#E8E8E8] text-[#2E2E2E]"
                  />
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    );
  }

  if (data?.orderStatus === OrderStatusEnum.PROCESSING && hasCustomerPaid) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">This Order has been accepted</p>
        {data.deliveryMethod === DeliveryType.BETTY_DELIVERY && (
          <OrderDriverSelection orderId={data.id} />
        )}
        {data.deliveryMethod === DeliveryType.UBER_DELIVERY && (
          <MarkAsShipped orderId={data.id} />
        )}
        {data.deliveryMethod === DeliveryType.SELF_DELIVERY && (
          <MarkAsShipped orderId={data.id} />
        )}
      </div>
    );
  }

  if (data?.orderStatus === "shipped") {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">
          This order is on it way to the shipping address
        </p>
        <div className="">
          <Button label="Mark as Missed" onClick={() => setModalOpen(true)} />
          <div>
            <Modal
              open={modalOpen}
              onCancel={() => setModalOpen(false)}
              footer={null}
              title="Mark Order as Missed"
            >
              <div className="py-2">
                <p className="text-primary-foreground text-lg">
                  Are you sure you want to mark this order as missed, this
                  action can not be reversed
                </p>

                <div className="">
                  <Textarea
                    placeholder="Reason for marking as missed"
                    name="note"
                    value={note}
                    onChange={(e: any) => setNote(e?.target?.value)}
                  />
                </div>

                <div className="flex space-x-2">
                  <Button
                    label="Yes"
                    isLoading={isPending}
                    onClick={handleMarkAsMissed}
                  />
                  <Button
                    label="No"
                    onClick={() => setModalOpen(false)}
                    className="bg-[#E8E8E8] text-[#2E2E2E]"
                  />
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    );
  }

  if (data?.orderStatus === OrderStatusEnum.DELIVERED) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-green-700">Violla!, This order has been completed</p>
      </div>
    );
  }

  if (data?.orderStatus === OrderStatusEnum.CANCELLED) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-[#FF0000]">This order has been cancelled</p>
      </div>
    );
  }

  if (data?.orderStatus === OrderStatusEnum.PLACED) {
    return (
      <div className="flex justify-between p-4 items-center">
        <p className="text-primary-foreground">
          Awaiting Payment from customer
        </p>
        <div className="">{/* <Button label="Assign for shipping" /> */}</div>
      </div>
    );
  }

  return null;
};
